import {
  Box, Button, Card, CardContent, CircularProgress, Grid, Typography
} from '@material-ui/core';
import { useAsync } from 'hooks/useAsync';
import { DestinationType } from 'models';
import React from 'react';
import { clientDestinationService } from 'services/clientDestinationService';

interface IClientDestinationVoteProps {
  notificationId: string;
}

const Row = ({ label, content }: {label?: string, content?:string}) => (
  <Grid container>
    <Grid item md={3}>
      <Typography variant="body2" align="right">
        {label}
      </Typography>
    </Grid>
    <Grid item md={9}>
      <Typography variant="body1" style={{ wordBreak: 'break-all' }}>
        {content}
      </Typography>
    </Grid>
  </Grid>
);

const getTitleByType = (type: DestinationType): string => {
  switch (type) {
    case DestinationType.WITHDRAWAL:
      return 'Approval of new withdrawal address';
    case DestinationType.NEWOWNER_PUBLICKEY:
      return 'Approval of new publickey for change of ownership';
    case DestinationType.TRANSACTION_SENDER:
      return 'Approval of new transaction sender address';
    default:
      return '';
  }
};

export const ClientDestinationVote = ({ notificationId }: IClientDestinationVoteProps) => {
  const details = useAsync(() => clientDestinationService.clientDestinationDetails(notificationId), { fetch: true });
  const reject = useAsync(() => clientDestinationService.rejectClientDestination(notificationId, details?.data?.blockchain!));
  const approve = useAsync(() => clientDestinationService.approveClientDestination(notificationId, details?.data?.blockchain!));

  if (!notificationId || (!details.hasExecuted)) {
    return <></>;
  }

  if (details.isError) {
    return <Typography variant="body1">{details?.error?.data[0]?.description}</Typography>;
  }

  const shouldDisable = approve.isLoading || approve.isSuccess || reject.isLoading || reject.isSuccess;

  return (
    <>
      <Box display="flex" pb={4}>
        <Typography variant="h3">
          {getTitleByType(details?.data?.destinationType!)}
        </Typography>
      </Box>
      <Card>
        <CardContent>
          <Grid container>
            <Grid item md={12}><Typography variant="h5">Details</Typography></Grid>
          </Grid>
          <Box pt={2}>
            <Row label="Address" content={details.data?.destinationPublicKey} />
            <Row label="Verified Label" content={details.data?.verifiedLabel} />
            <Row label="Blockchain" content={details.data?.blockchain} />
            <Row label="State" content={details.data?.state} />
          </Box>
          <Box pt={2}>
            <Grid container>
              <Grid item md={3} />
              <Grid item md={9}>
                {
                  approve.isSuccess || reject.isSuccess
                    ? <Typography variant="caption" color="textPrimary">Thanks for voting, your vote has been registered</Typography>
                    : (
                      <>
                        <Box display="flex" flexDirection="row">
                          <Box>
                            <Button
                              disabled={shouldDisable}
                              variant="contained"
                              color="secondary"
                              onClick={() => reject.exec()}
                              endIcon={(reject.isLoading) && <CircularProgress size="1rem" color="secondary" style={{ margin: 0 }} />}
                            >
                              Reject
                            </Button>
                          </Box>
                          <Box pl={2}>
                            <Button
                              disabled={shouldDisable}
                              variant="contained"
                              color="primary"
                              onClick={() => approve.exec()}
                              endIcon={(approve.isLoading) && <CircularProgress size="1rem" color="secondary" style={{ margin: 0 }} />}
                            >
                              Approve
                            </Button>
                          </Box>
                        </Box>
                        {approve.isError && approve.error
                          && <Typography variant="caption" color="primary">{approve?.error?.data[0]?.description}</Typography>}
                        {reject.isError && reject.error
                          && <Typography variant="caption" color="primary">{reject?.error?.data[0]?.description}</Typography>}
                      </>
                    )
                  }
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};
